
import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Autocomplete,
  Divider,
} from "@mui/material";

import { Input, InputDate, LabelComp } from "../../controls";
import { Alert, Form } from "../../layout";
import { DropLi, Model, UseForm } from "../../hooks";

import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";

import {
  IsEmptyObj,
  MaxLength,
  RmvEmptyProp,
  ToFormData,
} from "../../hooks/Method";

import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Salary Payment Preview
//------------------------------------------------
const SalaryPaymentPreview = ({
  user,
  values,
  store,
  genderName,
  salaryTypeName,
  t,
}) => {
  const money = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    return formatCurrency(Number(value), store);
  };

  const fields = [
    {
      label: t("firstName"),
      value: user?.firstName,
    },
    {
      label: t("lastName"),
      value: user?.lastName,
    },
    {
      label: t("dateOfBirth"),
      value: user?.dob,
    },
    {
      label: t("gender"),
      value: genderName,
    },
    {
      label: t("phone"),
      value: user?.phone,
    },
    {
      label: t("address"),
      value: user?.address,
    },
    {
      label: t("salaryType"),
      value: salaryTypeName,
    },
    {
      label: t("salary"),
      value: money(user?.salary),
    },
    {
      label: t("fromDate"),
      value: values?.fromDate,
    },
    {
      label: t("toDate"),
      value: values?.toDate,
    },
    {
      label: t("totalHours"),
      value: values?.totalHours,
    },
    {
      label: t("totalSalary"),
      value: money(values?.totalSalary),
    },
    {
      label: t("payDate"),
      value: values?.payDate,
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 1,
        }}
      >
        {t("salaryPaymentPreview")}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          mb: 3,
        }}
      >
        {t("salaryPaymentDraft")}
      </Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                minWidth: "35%",
              }}
            >
              {field.label}:
            </Typography>

            <Typography
              sx={{
                textAlign: "right",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {field.value === null ||
              field.value === undefined ||
              field.value === ""
                ? "-"
                : field.value}
            </Typography>
          </Box>

          <Divider />
        </React.Fragment>
      ))}
    </Box>
  );
};

//------------------------------------------------
// Salary Payment
//------------------------------------------------
const SalaryPayment = (props) => {
  const { t } = useTranslation();

  const { _post, _get, _clear, _store } = props;

  const { PaymentMdl } = Model();
  const { Gender, Periodic } = DropLi();

  const reportRef = useRef(null);

  const [searchLi, setSearchLi] = useState([]);
  const [user, setUser] = useState({});
  const [err, setErr] = useState();

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  const {
    values,
    errors,
    setErrors,
    handleInpChg,
    handleInpDate,
    resetFormControls,
  } = UseForm(PaymentMdl);

  //------------------------------------------------
  // Employee Information
  //------------------------------------------------
  const genderName = Gender.find(
    (item) => String(item.id) === String(user.gender)
  )?.name || "";

  const salaryTypeName = Periodic.find(
    (item) => String(item.id) === String(user.salaryType)
  )?.name || "";

  //------------------------------------------------
  // Validation
  //------------------------------------------------
  const validateForm = () => {
    const temp = {};

    temp.fromDate =
      values.fromDate !== ""
        ? ""
        : t("fromDateRequired");

    temp.toDate =
      values.toDate !== ""
        ? ""
        : t("toDateRequired");

    temp.totalSalary =
      values.totalSalary !== ""
        ? ""
        : t("totalSalaryRequired");

    temp.payDate =
      values.payDate !== ""
        ? ""
        : t("payDateRequired");

    setErrors({ ...temp });

    return Object.values(temp).every(
      (value) => value === ""
    );
  };

  //------------------------------------------------
  // Submit Salary Payment
  //------------------------------------------------
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm() && user.id) {
      const paymentValues = {
        ...values,
        userId: user.id,
        storeId: _store.id,
      };

      const onComplete = safeOnComplete(
        (res) => {
          if (res.status === 201) {
            setAlert({
              isOpen: true,
              type: "success",
              title: t("successful"),
              subTitle: res.msg,
            });

            resetFormControls();
            setUser({});
            setErr();
          } else {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: t("somethingWentWrong"),
            });
          }
        },
        (error) => {
          setAlert({
            isOpen: true,
            type: "error",
            title: t("error"),
            subTitle: error.msg,
          });
        }
      );

      _post(
        endpoints.Salary,
        ToFormData(RmvEmptyProp(paymentValues)),
        null,
        onComplete
      );
    } else if (!user.id) {
      setErr(t("noUserSelected"));
    }
  };

  //------------------------------------------------
  // Select Employee
  //------------------------------------------------
  const handleSelect = (val) => {
    if (val === null) {
      setUser({});
    } else {
      setUser(val);
      setErr();
    }
  };

  //------------------------------------------------
  // Load Employees
  //------------------------------------------------
  useEffect(() => {
    const onComplete = (res) => {
      if (res.status === 200) {
        setSearchLi(
          res.result.map((item) => ({
            ...item,
            label:
              `${item.firstName} ${item.lastName} ` +
              `(${item.phone})`,
          }))
        );
      }
    };

    _get(endpoints.User, null, onComplete);

    return () => {
      _clear();
      _clear({}, actions.ActionType.ClearData);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store]);

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      <Form noValidate onSubmit={handleSubmit}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
          }}
        >
          <Typography
            variant="h5"
            className="fs-26 gray-color fw-5"
          >
            {t("salaryPaymentForm")}
          </Typography>

          {/* Print Preview */}
          <ReportPrintButton
            reportRef={reportRef}
            paperSize="Letter"
            reportTitle={t("salaryPaymentPreview")}
          />
        </Box>

        <div className="bs-bottom mx-w-80 mt-5 m-auto" />

        {/* Error */}
        {err && (
          <Grid item xs={12}>
            <Typography color="error">
              <span className="tx-upp fw-6">
                {t("errors")}:
              </span>{" "}
              {err}
            </Typography>
          </Grid>
        )}

        {/* Search Employee */}
        <Paper className="shadow-xs br-8 p-15 mt-20">
          <Autocomplete
            options={searchLi}
            classes={{
              paper: "br-6 shadow-sm",
            }}
            onChange={(e, newVal) =>
              handleSelect(newVal)
            }
            value={IsEmptyObj(user) ? null : user}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id
            }
            renderInput={(params) => (
              <Input
                {...params}
                label={t("searchEmployeeByNameOrPhone")}
              />
            )}
          />
        </Paper>

        {/* Employee Information */}
        <div className="pt-20" />

        <Paper className="mt-30 shadow-xs br-8">
          <Grid
            container
            spacing={3}
            className="p-20 pt-0"
          >
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                className="fs-18 fw-5 gray-color pl-10"
                sx={{
                  borderLeft:
                    "5px double var(--primary-color)",
                }}
              >
                {t("userInformation")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <LabelComp
                label={t("firstName")}
                value={user.firstName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LabelComp
                label={t("lastName")}
                value={user.lastName}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <LabelComp
                label={t("dateOfBirth")}
                value={user.dob}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <LabelComp
                label={t("gender")}
                value={genderName}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <LabelComp
                label={t("phone")}
                value={user.phone}
              />
            </Grid>

            <Grid item xs={12}>
              <LabelComp
                label={t("address")}
                value={user.address}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LabelComp
                label={t("salaryType")}
                value={salaryTypeName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LabelComp
                label={t("salary")}
                value={
                  user.salary === null ||
                  user.salary === undefined ||
                  user.salary === ""
                    ? ""
                    : formatCurrency(
                        Number(user.salary),
                        _store
                      )
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Pay Period */}
        <div className="pt-20" />

        <Paper className="mt-30 shadow-xs br-8">
          <Grid
            container
            spacing={3}
            className="p-20 pt-0"
          >
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                className="fs-18 fw-5 gray-color pl-10"
                sx={{
                  borderLeft:
                    "5px double var(--primary-color)",
                }}
              >
                {t("payPeriod")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InputDate
                label={t("fromDate")}
                name="fromDate"
                value={values.fromDate}
                error={errors.fromDate}
                onChange={(date) =>
                  handleInpDate(
                    "fromDate",
                    new Date(date).toLocaleDateString()
                  )
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputDate
                label={t("toDate")}
                name="toDate"
                value={values.toDate}
                error={errors.toDate}
                onChange={(date) =>
                  handleInpDate(
                    "toDate",
                    new Date(date).toLocaleDateString()
                  )
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Salary Payment */}
        <div className="pt-20" />

        <Paper className="mt-30 shadow-xs br-8">
          <Grid
            container
            spacing={3}
            className="p-20 pt-0"
          >
            <Grid item xs={12} md={4}>
              <Input
                label={t("totalHours")}
                name="totalHours"
                type="number"
                onInput={(e) =>
                  (e.target.value = MaxLength(
                    e.target.value,
                    9
                  ))
                }
                value={values.totalHours}
                error={errors.totalHours}
                onChange={handleInpChg}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Input
                label={t("totalSalary")}
                name="totalSalary"
                type="number"
                onInput={(e) =>
                  (e.target.value = MaxLength(
                    e.target.value,
                    9
                  ))
                }
                value={values.totalSalary}
                error={errors.totalSalary}
                onChange={handleInpChg}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputDate
                label={t("payDate")}
                name="payDate"
                value={values.payDate}
                error={errors.payDate}
                onChange={(date) =>
                  handleInpDate(
                    "payDate",
                    new Date(date).toLocaleDateString()
                  )
                }
                fullWidth
              />
            </Grid>

            {/* Submit */}
            <Grid
              item
              xs={12}
              className="tx-center"
            >
              <Button
                type="submit"
                className="btn-secondary min-w-150"
                size="large"
              >
                {t("submit")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Form>

      {/* Print-only Salary Payment Preview */}
      <Box
        ref={reportRef}
        className="salary-payment-form-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <SalaryPaymentPreview
          user={user}
          values={values}
          store={_store}
          genderName={genderName}
          salaryTypeName={salaryTypeName}
          t={t}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  _data: state.pos.data,
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _post: actions.post,
  _put: actions.put,
  _getById: actions.getById,
  _get: actions.get,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(SalaryPayment);
